import type { VehicleSide } from "@/lib/api/types"

// Quick Estimate 3 — service-line types.
//
// QE3 has its own photo shape (QEPhoto) instead of reusing QE2's
// CapturedShot. Reasons:
//   • per-photo description (the BE supports `images[i].description`
//     and the inspect lightbox lets the advisor type one)
//   • verdict surface for quality issues (so the photo step can show
//     the AI's "too dark / blurry / wrong-side" feedback inline)
//   • sideOverridden flag so the AI tag chip can show "1 corrected"
//     when the advisor manually re-tagged a side
//   • sendWithoutAiTag flag for photos the AI never managed to
//     evaluate but the advisor wants to ship anyway

export type {
  VinDecodedVehicle,
  BodySilhouette,
} from "../wizard-shared/vehicle-types"
export { silhouetteFor } from "../wizard-shared/vehicle-types"

// Surfaced on each photo tile. Drives the verdict overlay copy + the
// quality dot color. `eval-failed` is special — the AI never returned
// a result; the advisor can retry or send without an AI tag.
export type QEVerdict =
  | "good"
  | "too-close"
  | "too-dark"
  | "blurry"
  | "wrong-side" // not used in QE3 (no expected side) — kept for parity
  | "low-quality"
  | "eval-failed"

export interface QEPhoto {
  // Local id — used as React key + to match a File reference held in
  // component state during in-flight + retry windows.
  id: string
  previewUrl: string
  // CDN url returned by /agents/images/evaluate. Empty string means
  // the AI evaluation didn't run successfully — retry or
  // sendWithoutAiTag is the recovery path.
  uploadedUrl: string
  detectedSide: VehicleSide
  qualityScore: number
  qualified: boolean
  qualityIssues: string[]
  takenAt: number
  verdict: QEVerdict
  // Optional per-photo notes the advisor types in the inspect
  // lightbox. Sent on the BE as `images[i].description`.
  description?: string
  // Set when the advisor manually corrected the AI's side guess.
  // Surfaces a small "edited" badge on the tile and changes the
  // copy in the AI-tag chip.
  sideOverridden?: boolean
  // Set when the advisor accepts a photo whose AI evaluation kept
  // failing. Photos with this flag are submitted with side
  // FRONT_CENTER as a fallback (the BE accepts that — if it ever
  // matters we can extend the addImages DTO).
  sendWithoutAiTag?: boolean
  // Number of times the advisor has hit "Retry" on this photo's
  // failed evaluation. Capped at 3 in the photo step.
  retryCount: number
  // Links a photo to the damage panel it was taken for (guided capture).
  // Lets the guided step group shots per panel + track coverage.
  taggedPanelId?: string
}

// Quick Estimate uses a 5-option side picker — Front, Driver,
// Passenger, Rear, Roof. The BE accepts all 13 VehicleSide values,
// but advisors don't reliably tell e.g. "front-left side" from
// "front-left corner" looking at a photo. Five buckets mirrors how
// people actually think about damage angles. Anything finer the AI
// returns gets normalized into one of the 5 on intake.

export type BasicSide = Extract<
  VehicleSide,
  "FRONT_CENTER" | "REAR_CENTER" | "LEFT_SIDE" | "RIGHT_SIDE" | "ROOF"
>

// Maps any of the 13 VehicleSide values down to one of the 5 basics.
// Front/rear corners collapse to the matching center; per-axle side
// sides (FRONT_LEFT_SIDE etc) collapse to the simple driver/passenger.
export function basicSideOf(side: VehicleSide): BasicSide {
  switch (side) {
    case "FRONT_CENTER":
    case "FRONT_LEFT_CORNER":
    case "FRONT_RIGHT_CORNER":
      return "FRONT_CENTER"
    case "REAR_CENTER":
    case "REAR_LEFT_CORNER":
    case "REAR_RIGHT_CORNER":
      return "REAR_CENTER"
    case "LEFT_SIDE":
    case "FRONT_LEFT_SIDE":
    case "REAR_LEFT_SIDE":
      return "LEFT_SIDE"
    case "RIGHT_SIDE":
    case "FRONT_RIGHT_SIDE":
    case "REAR_RIGHT_SIDE":
      return "RIGHT_SIDE"
    case "ROOF":
      return "ROOF"
  }
}

export interface SideOption {
  value: BasicSide
  label: string
  /** Friendly noun for coverage hints ("Consider a {noun} shot"). */
  noun: string
}

// Display order matches how the picker is laid out: front-first,
// then driver/passenger (the two most-photographed angles in
// collision triage), rear, roof.
export const SIDE_OPTIONS: SideOption[] = [
  { value: "FRONT_CENTER", label: "Front", noun: "front" },
  { value: "LEFT_SIDE", label: "Driver", noun: "driver-side" },
  { value: "RIGHT_SIDE", label: "Passenger", noun: "passenger-side" },
  { value: "REAR_CENTER", label: "Rear", noun: "rear" },
  { value: "ROOF", label: "Roof", noun: "roof" },
]

// Compact label for any VehicleSide. Normalizes via basicSideOf so
// raw 13-value AI tags collapse to one of the 5 display labels.
export function shortSideLabel(side: VehicleSide | null | undefined): string {
  if (!side) return "—"
  const basic = basicSideOf(side)
  return SIDE_OPTIONS.find((o) => o.value === basic)?.label ?? "Photo"
}

// Shared sessionStorage draft for both Quick Estimate (/estimates/quick)
// and Full Estimate (/estimates/full). The two wizards have identical
// inputs (vehicle / photos / customer / notes) so they share a single
// draft — starting one and switching to the other carries your work.
// Survives in-tab refreshes but dies on tab close, per the "service
// advisor finishes in one sitting" model. previewUrl persists in the
// draft because we re-seed it from uploadedUrl on hydration; File
// refs cannot be persisted.
export const ESTIMATE_DRAFT_KEY = "etx_estimate_draft_v1"
export interface QEDraft {
  v: 2
  vin: string
  vehicle: import("../wizard-shared/vehicle-types").VinDecodedVehicle | null
  photos: QEPhoto[]
  // Pre-photo damage tags from the 3D tagger (quick-estimate only).
  damageTags?: import("./damage-tag/panels").DamageTag[]
  notes: string
  customer: { name: string; phone: string }
  step: 0 | 1 | 2 | 3
  updatedAt: number
}

// Recent customer cache — fork of QE2's. Service line uses this to
// one-tap pre-fill repeat customers. 30-min TTL.
export const RECENT_CUSTOMER_KEY = "etx_recent_customer_v1"
export interface QERecentCustomer {
  name: string
  phone: string
  ts: number
}
